import { mapService } from '@/services/map/mapService.js'
import ImageLayer from 'ol/layer/Image.js'
import { getPointResolution } from 'ol/proj.js'
import { ImageArcGISRest, ImageStatic, ImageWMS, TileArcGISRest } from 'ol/source.js'
import moment from 'moment'

export const MAX_PROXY_LAYERS = 8
const MAX_PARALLEL_PROXY_REQUESTS = 3
const RENDER_TIMEOUT_MS = 20000
const MAX_PRINT_WIDTH = 1920
const MAX_PRINT_HEIGHT = 1080
const MAX_PRINT_PIXELS = 2073600
const SCALE_LINE_MIN_WIDTH = 64

// Estas fuentes se reemplazan solo mientras se prepara el mapa para exportar. ArcGIS
// raster no permite leer sus pixeles en el navegador y GeoServer raster se
// creo historicamente sin crossOrigin. FeatureServer y las capas vectoriales
// locales siguen dibujandose directamente en el frontend.
function needsPrintProxy(layer) {
  const source = layer.getSource && layer.getSource()
  if (source instanceof TileArcGISRest || source instanceof ImageArcGISRest) return true
  return source instanceof ImageWMS && layer.get('provider') === 'geoserver'
}

function activeProxyLayers(activeLayers) {
  const map = mapService.map
  if (!map) return []
  const activeByID = new Map(
    (activeLayers || [])
      .filter(layer => layer && layer.id !== undefined && layer.id !== null)
      .map(layer => [String(layer.id), layer])
  )
  return map.getLayers().getArray()
    .filter(layer => {
      const id = layer.get('id')
      return id !== undefined && id !== null && layer.getVisible() &&
        activeByID.has(String(id)) && needsPrintProxy(layer)
    })
    .map(layer => ({ layer, descriptor: activeByID.get(String(layer.get('id'))) }))
}

// Nombres informativos de las capas que se obtendran mediante el backend al
// exportar. Ya no son capas incompatibles: solo cambian de origen durante la captura.
export function proxiedLayerNames(activeLayers) {
  return activeProxyLayers(activeLayers)
    .map(item => item.descriptor && item.descriptor.name)
    .filter(Boolean)
}

function stringParam(params, ...names) {
  for (const name of names) {
    const value = params[name]
    if (value !== undefined && value !== null && value !== '') {
      return typeof value === 'string' ? value : JSON.stringify(value)
    }
  }
  return ''
}

function renderOptions(layer) {
  const source = layer.getSource && layer.getSource()
  const params = source && typeof source.getParams === 'function' ? source.getParams() : {}
  return {
    layer_defs: stringParam(params, 'LAYERDEFS', 'layerDefs'),
    // Al exportar se reproduce la simbologia publicada por el
    // proveedor. dynamicLayers corresponde a la personalizacion elegida en el
    // visor y se omite deliberadamente tanto para la imagen como para la leyenda.
    dynamic_layers: '',
    spatial_filter: stringParam(params, 'spatialFilter', 'SPATIALFILTER'),
    cql_filter: stringParam(params, 'CQL_FILTER'),
    mosaic_rule: stringParam(params, 'mosaicRule', 'MOSAICRULE'),
    rendering_rule: stringParam(params, 'renderingRule', 'RENDERINGRULE')
  }
}

// FeatureServer se dibuja como vector en el navegador y no pasa por el proxy.
// Si tiene una simbologia personalizada, se restaura temporalmente el estilo
// original durante la captura y luego se devuelve exactamente al estado visual
// que tenia el usuario.
function activeCustomVectorStyles(activeLayers) {
  const map = mapService.map
  if (!map) return []
  const activeByID = new Map(
    (activeLayers || [])
      .filter(layer => layer && layer.id !== undefined && layer.id !== null)
      .map(layer => [String(layer.id), layer])
  )

  return map.getLayers().getArray().reduce((styles, layer) => {
    const id = String(layer.get('id') || '')
    const descriptor = activeByID.get(id)
    if (!descriptor || descriptor.provider?.name !== 'arcgis' || !layer.getVisible() ||
      needsPrintProxy(layer) || typeof layer.getStyle !== 'function') {
      return styles
    }
    const defaultStyle = layer.get('defaultStyle')
    const currentStyle = layer.getStyle()
    if (defaultStyle === undefined || currentStyle === defaultStyle) return styles
    styles.push({ layer, defaultStyle, currentStyle })
    return styles
  }, [])
}

async function mapWithConcurrency(items, concurrency, worker) {
  const results = new Array(items.length)
  let nextIndex = 0
  const run = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++
      results[index] = await worker(items[index], index)
    }
  }
  const runners = []
  for (let i = 0; i < Math.min(concurrency, items.length); i++) runners.push(run())
  await Promise.all(runners)
  return results
}

async function prepareProxyLayer(item, context) {
  const id = String(item.layer.get('id'))
  const response = await context.requestLayerImage({
    layerId: id,
    payload: {
      map_id: context.mapId,
      extent: context.extent,
      projection: context.projection,
      width: context.outputSize[0],
      height: context.outputSize[1],
      options: renderOptions(item.layer)
    }
  })
  const blob = response && response.data
  if (!(blob instanceof Blob) || !blob.type.startsWith('image/')) {
    throw new Error('invalid-proxy-image')
  }

  const objectUrl = URL.createObjectURL(blob)
  const proxyLayer = new ImageLayer({
    // Una clase distinta impide que OpenLayers reutilice para el proxy el
    // mismo contenedor/canvas que uso previamente la capa remota.
    className: 'ol-layer print-proxy-layer',
    source: new ImageStatic({
      url: objectUrl,
      imageExtent: context.extent,
      projection: context.projection,
      imageSize: context.outputSize
    }),
    opacity: item.layer.getOpacity(),
    visible: true,
    extent: item.layer.getExtent()
  })
  proxyLayer.setZIndex(item.layer.getZIndex())
  proxyLayer.set('printProxy', true)
  proxyLayer.set('id', `print-proxy-${id}`)
  const clipGeometry = item.layer.get('printClipGeometry')
  if (clipGeometry) mapService.applyLayerClipping(proxyLayer, clipGeometry)
  return { ...item, proxyLayer, objectUrl }
}

function waitForMapRender(map) {
  return new Promise(resolve => {
    let finished = false
    const finish = timedOut => {
      if (finished) return
      finished = true
      clearTimeout(timer)
      map.un('rendercomplete', onComplete)
      resolve(timedOut)
    }
    const onComplete = () => finish(false)
    const timer = setTimeout(() => finish(true), RENDER_TIMEOUT_MS)
    map.once('rendercomplete', onComplete)
    map.renderSync()
  })
}

function waitForLayoutPaint() {
  return new Promise(resolve => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
  })
}

// OpenLayers conserva internamente el tamaño del ultimo layout conocido. Antes
// de cada ejecucion se fuerza su actualizacion, se espera que el navegador
// aplique el layout y se copian tamaño y extension para que ninguna variacion
// posterior del DOM cambie los parametros de una captura que ya comenzo. El
// render completo se espera mas abajo, una vez instaladas las capas temporales.
async function snapshotMapViewport(map) {
  map.updateSize()
  await waitForLayoutPaint()
  map.updateSize()

  const currentSize = map.getSize()
  if (!currentSize || currentSize.length !== 2 || currentSize[0] < 1 || currentSize[1] < 1) {
    throw new Error('map-size-invalid')
  }
  const size = Object.freeze([Number(currentSize[0]), Number(currentSize[1])])
  const view = map.getView()
  const projection = view.getProjection().getCode()
  const extent = Object.freeze([...view.calculateExtent(size)])
  return { size, projection, extent }
}

// Un canvas conserva su estado "tainted" aunque OpenLayers lo limpie con
// clearRect. Reasignar su ancho reinicializa el bitmap y recupera el estado
// origin-clean. Esto se ejecuta cuando las capas remotas ya estan ocultas y
// los reemplazos seguros ya fueron agregados, antes del render de captura.
function resetMapCanvases(map) {
  map.getViewport()
    .querySelectorAll('.ol-layer canvas, canvas.ol-layer')
    .forEach(canvas => {
      canvas.width = canvas.width
    })
}

// Leer un pixel revela si algun proveedor externo contamino un canvas. Se
// valida antes de copiar: una vez mezclado, el PNG completo seria ilegible.
function isCanvasExportable(canvas) {
  try {
    const context = canvas.getContext('2d')
    if (!context) return false
    context.getImageData(0, 0, 1, 1)
    return true
  } catch (error) {
    return false
  }
}

function canvasTransform(canvas, mapSize) {
  const transform = canvas.style.transform
  const match = transform && transform.match(/^matrix\(([^)]*)\)$/)
  if (match) {
    const matrix = match[1].split(',').map(Number)
    if (matrix.length === 6 && matrix.every(Number.isFinite)) return matrix
  }
  return [canvas.width / mapSize[0], 0, 0, canvas.height / mapSize[1], 0, 0]
}

function limitedOutputSize(mapSize) {
  const pixelScale = Math.sqrt(MAX_PRINT_PIXELS / (mapSize[0] * mapSize[1]))
  const scale = Math.min(1, MAX_PRINT_WIDTH / mapSize[0], MAX_PRINT_HEIGHT / mapSize[1], pixelScale)
  return [Math.max(1, Math.round(mapSize[0] * scale)), Math.max(1, Math.round(mapSize[1] * scale))]
}

function scaleLineDistance(metersPerPixel, minWidth) {
  if (!Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return null
  const minDistance = metersPerPixel * minWidth
  const magnitude = Math.pow(10, Math.floor(Math.log10(minDistance)))
  for (const multiplier of [1, 2, 5, 10]) {
    const distance = multiplier * magnitude
    if (Math.round(distance / metersPerPixel) >= minWidth) return distance
  }
  return 10 * magnitude
}

// La escala se calcula en el centro del viewport para considerar la distorsión
// propia de la proyección (por ejemplo Web Mercator). Se replica el ancho mínimo
// predeterminado del ScaleLine visible de OpenLayers, de modo que ambos elijan
// la misma distancia redonda; luego se adapta solo el ancho al PNG final.
function buildScaleBar(map, mapSize, outputSize) {
  const view = map.getView()
  const resolution = view.getResolution()
  const center = view.getCenter()
  const projection = view.getProjection()
  if (!Number.isFinite(resolution) || !center || !projection) return null

  const metersPerCssPixel = getPointResolution(projection, resolution, center, 'm')
  const distanceMeters = scaleLineDistance(metersPerCssPixel, SCALE_LINE_MIN_WIDTH)
  if (!distanceMeters || !Number.isFinite(metersPerCssPixel) || metersPerCssPixel <= 0) return null

  const cssWidth = distanceMeters / metersPerCssPixel
  const widthPixels = cssWidth * (outputSize[0] / mapSize[0])
  const label = distanceMeters >= 1000
    ? `${distanceMeters / 1000} km`
    : `${Math.round(distanceMeters)} m`
  return {
    distance_meters: distanceMeters,
    width_pixels: Math.round(widthPixels),
    label
  }
}

function scaledTransform(matrix, mapSize, outputSize) {
  const scaleX = outputSize[0] / mapSize[0]
  const scaleY = outputSize[1] / mapSize[1]
  return [
    matrix[0] * scaleX,
    matrix[1] * scaleY,
    matrix[2] * scaleX,
    matrix[3] * scaleY,
    matrix[4] * scaleX,
    matrix[5] * scaleY
  ]
}

function composeMapCanvases(map, mapSize, outputSize) {
  const canvas = document.createElement('canvas')
  canvas.width = outputSize[0]
  canvas.height = outputSize[1]
  const context = canvas.getContext('2d')
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  let omittedCanvasCount = 0

  map.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer').forEach(layerCanvas => {
    if (layerCanvas.width <= 0 || layerCanvas.height <= 0) return
    const layerContainer = layerCanvas.closest && layerCanvas.closest('.ol-layer')
    if (layerContainer && window.getComputedStyle(layerContainer).display === 'none') return
    if (!isCanvasExportable(layerCanvas)) {
      omittedCanvasCount += 1
      return
    }
    try {
      const parentOpacity = layerCanvas.parentNode && layerCanvas.parentNode.style.opacity
      context.save()
      context.globalAlpha = parentOpacity === '' || parentOpacity === undefined
        ? 1
        : Number(parentOpacity)
      context.setTransform(...scaledTransform(canvasTransform(layerCanvas, mapSize), mapSize, outputSize))
      context.drawImage(layerCanvas, 0, 0)
      context.restore()
    } catch (error) {
      context.restore()
      omittedCanvasCount += 1
    }
  })

  return { mapImage: canvas.toDataURL('image/png'), omittedCanvasCount }
}

// Captura el viewport actual. Solo las capas raster persistidas que requieren
// proxy son solicitadas al backend; bases, vectores, dibujos y anotaciones
// permanecen en el frontend. Los reemplazos son efimeros y siempre se limpian.
export async function captureMap({ mapId, activeLayers, requestLayerImage }) {
  const map = mapService.map
  if (!map) throw new Error('map-not-ready')
  if (!mapId || typeof requestLayerImage !== 'function') throw new Error('print-proxy-not-ready')

  const viewport = await snapshotMapViewport(map)
  const { size, projection, extent } = viewport
  const outputSize = limitedOutputSize(size)
  const scaleBar = buildScaleBar(map, size, outputSize)
  const candidates = activeProxyLayers(activeLayers)
  const selected = candidates.slice(0, MAX_PROXY_LAYERS)
  const overflow = candidates.slice(MAX_PROXY_LAYERS)
  const proxyWarnings = overflow.map(item => ({
    layerId: String(item.layer.get('id')),
    layerName: item.descriptor.name,
    reason: 'limit'
  }))
  const prepared = []
  const customVectorStyles = activeCustomVectorStyles(activeLayers)

  try {
    const results = await mapWithConcurrency(selected, MAX_PARALLEL_PROXY_REQUESTS, async item => {
      try {
        return await prepareProxyLayer(item, { mapId, extent, projection, outputSize, requestLayerImage })
      } catch (error) {
        console.warn(`[print] no se pudo obtener la capa ${item.descriptor.name}`, error)
        proxyWarnings.push({
          layerId: String(item.layer.get('id')),
          layerName: item.descriptor.name,
          reason: 'provider'
        })
        return { ...item, proxyLayer: null, objectUrl: null }
      }
    })
    prepared.push(...results)

    // El cambio visual ocurre solo despues de terminar las descargas.
    candidates.forEach(item => item.layer.setVisible(false))
    prepared.forEach(item => {
      if (item.proxyLayer) map.addLayer(item.proxyLayer)
    })
    customVectorStyles.forEach(item => item.layer.setStyle(item.defaultStyle))

    // Reiniciar el bitmap solo es necesario cuando se ocultaron capas raster
    // remotas que pudieron contaminar un canvas. Si no hay ninguna, se conserva
    // el canvas ya renderizado del mapa base; reiniciarlo podía producir una
    // captura vacía al exportar sin capas temáticas activas.
    if (candidates.length > 0) resetMapCanvases(map)
    const renderTimedOut = await waitForMapRender(map)
    if (renderTimedOut) {
      proxyWarnings.push({ layerId: '', layerName: 'Mapa', reason: 'render-timeout' })
    }
    const capture = composeMapCanvases(map, size, outputSize)
    return {
      ...capture,
      proxyWarnings,
      proxiedLayerCount: prepared.filter(item => item.proxyLayer).length,
      renderTimedOut,
      scaleBar
    }
  } finally {
    prepared.forEach(item => {
      if (item.proxyLayer) map.removeLayer(item.proxyLayer)
      if (item.objectUrl) URL.revokeObjectURL(item.objectUrl)
    })
    candidates.forEach(item => item.layer.setVisible(true))
    customVectorStyles.forEach(item => item.layer.setStyle(item.currentStyle))
    map.renderSync()
  }
}

// Referencias de las capas realmente visibles al momento de exportar. No se
// envian URLs: el backend autoriza cada id y resuelve el proveedor desde Mongo.
export function buildLegendLayerRequests(activeLayers, proxyWarnings = []) {
  const map = mapService.map
  if (!map) return []

  const activeByID = new Map(
    (activeLayers || [])
      .filter(layer => layer && layer.id !== undefined && layer.id !== null)
      .map(layer => [String(layer.id), layer])
  )
  const omittedIDs = new Set(
    (proxyWarnings || [])
      .map(warning => String(warning.layerId || ''))
      .filter(Boolean)
  )
  const addedIDs = new Set()

  return map.getLayers().getArray().reduce((requests, olLayer) => {
    const id = String(olLayer.get('id') || '')
    const descriptor = activeByID.get(id)
    const provider = descriptor && descriptor.provider && descriptor.provider.name
    if (!/^[a-f\d]{24}$/i.test(id) || addedIDs.has(id) || omittedIDs.has(id) ||
      !olLayer.getVisible() || (provider !== 'geoserver' && provider !== 'arcgis')) {
      return requests
    }
    addedIDs.add(id)
    requests.push({ layer_id: id, options: renderOptions(olLayer) })
    return requests
  }, [])
}

export function buildMetadata({ mapName, activeLayers, geoprocessData, proxyWarnings = [] }) {
  moment.locale('es')
  const lines = ['Fecha: ' + moment(new Date()).format('LLL')]
  if (mapName) lines.push('Mapa: ' + mapName)
  const layerNames = (activeLayers || []).map(layer => layer.name).filter(Boolean)
  if (layerNames.length) lines.push('Capas activas: ' + layerNames.join(', '))
  if (geoprocessData && Object.keys(geoprocessData).length) lines.push('Geoproceso activo')
  const omittedNames = proxyWarnings.map(warning => warning.layerName).filter(name => name && name !== 'Mapa')
  if (omittedNames.length) lines.push('Capas no incluidas: ' + [...new Set(omittedNames)].join(', '))
  if (proxyWarnings.some(warning => warning.reason === 'render-timeout')) {
    lines.push('Advertencia: la captura alcanzo el tiempo maximo de espera')
  }
  return lines
}

// Versión estructurada del contexto para que el generador de PDF pueda
// presentarlo visualmente sin tener que interpretar las líneas de metadata.
// metadata se conserva en el contrato por compatibilidad y para la salida JPG.
export function buildPrintContext({ mapName, activeLayers, geoprocessData, proxyWarnings = [] }) {
  moment.locale('es')
  const activeLayerNames = [...new Set(
    (activeLayers || []).map(layer => layer.name).filter(Boolean)
  )]
  const omittedLayerNames = [...new Set(
    (proxyWarnings || [])
      .map(warning => warning.layerName)
      .filter(name => name && name !== 'Mapa')
  )]

  return {
    generated_at: moment(new Date()).format('LLL'),
    map_name: mapName || 'Mapa sin nombre',
    active_layers: activeLayerNames,
    active_layer_count: activeLayerNames.length,
    geoprocess_active: Boolean(geoprocessData && Object.keys(geoprocessData).length),
    omitted_layers: omittedLayerNames
  }
}
